// src/components/ChunkErrorBoundary.tsx
// Safety net for lazy-loaded routes (code-splitting, build-plan
// docs/build-plan/bundle-code-splitting.md).
//
// When a lazy() dynamic import fails — almost always a "stale chunk" after a
// deploy replaced the content-hashed files while a tab was open, or the 30s
// index.html cache still points at old chunk names — we recover with ONE full
// page reload, which fetches a fresh index.html + the new chunk names.
//
// Loop-safe: we only auto-reload if the last auto-reload was more than
// RELOAD_GAP_MS ago (or never). So each deploy gets one clean recovery, but a
// genuinely broken chunk (the reload fails again immediately) falls through to
// a manual "Reload" prompt instead of looping forever.

import { Component, type ReactNode } from "react";
import { C, F } from "../theme";

const RELOAD_TS = "nymr-chunk-reload-ts";
const RELOAD_GAP_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error)) || "";
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk [\w-]+ failed|dynamically imported module/i.test(msg);
}

export class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error)) {
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(RELOAD_TS) || "0");
      } catch {
        last = 0;
      }
      if (Date.now() - last > RELOAD_GAP_MS) {
        try {
          sessionStorage.setItem(RELOAD_TS, String(Date.now()));
        } catch {
          /* sessionStorage unavailable — still attempt one reload below */
        }
        window.location.reload();
      }
    }
    // Otherwise (a non-chunk error, or a chunk error that already survived a
    // reload), leave state.failed = true and show the manual prompt below.
  }

  render(): ReactNode {
    if (this.state.failed) {
      return (
        <div style={{ padding: "48px 24px", textAlign: "center", fontFamily: F.body, color: C.inkSoft }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: C.ink, marginBottom: 6 }}>Couldn&rsquo;t load this page</div>
          <div style={{ fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>A new version may have just been released. Reloading usually fixes it.</div>
          <button
            onClick={() => { try { sessionStorage.removeItem(RELOAD_TS); } catch { /* ignore */ } window.location.reload(); }}
            style={{ padding: "10px 18px", borderRadius: 11, border: "none", background: C.brassDk, color: C.brassGlow, fontFamily: F.body, fontWeight: 800, fontSize: 14, cursor: "pointer" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
